import { useEffect, useState } from "react";
import styles from "./InstallPrompt.module.css";

// Minimal type for the deferred beforeinstallprompt event — Chrome/Edge only.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "seeker.install.dismissedAt";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari-specific
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
}

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < DISMISS_WINDOW_MS;
}

// Bottom sheet that either fires the native install prompt (Android/Chrome)
// or shows the iOS Safari "Share → Home Screen" hint. Auto-hides for
// standalone launches, dismissals persist for 14 days.
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasDismissedRecently()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIos()) {
      // No beforeinstallprompt on iOS Safari — surface a manual hint instead.
      setShowIosHint(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "dismissed") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferred(null);
  };

  if (deferred) {
    return (
      <div className={styles.sheet} role="dialog" aria-label="Установка приложения">
        <div className={styles.copy}>
          <div className={styles.title}>Установить «Паспорт искателя»</div>
          <div className={styles.hint}>
            Иконка на главном экране — открывается как обычное приложение.
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={dismiss}>
            Позже
          </button>
          <button type="button" className={styles.primary} onClick={install}>
            Установить
          </button>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className={styles.sheet} role="dialog" aria-label="Установка приложения">
        <div className={styles.copy}>
          <div className={styles.title}>Добавьте на экран «Домой»</div>
          <div className={styles.hint}>
            Нажмите <span aria-label="Поделиться">⎋</span> Поделиться → «На экран
            «Домой»» — паспорт будет открываться как приложение.
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={dismiss}>
            Понятно
          </button>
        </div>
      </div>
    );
  }

  return null;
}
